import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { Store } from '@ngrx/store';

import { environment } from 'src/environments/environment';
import { AppState } from 'src/app/store';
import { AnalyticsService } from 'src/app/core/analytics/analytics.service';

import {
  selectIsAuthenticated,
  selectIdTokenClaims,
} from '../store/selectors/msal-auth.selectors';
import { extractParticipantIdFromToken } from '../utils/token.utils';
import { MsalAuthActions } from '../store/actions/msal-auth.actions';
import { CustomAuthErrorCodes } from '../config/constants';

export const msalAuthGuard: CanActivateFn = (route) => {
  const platformId = inject(PLATFORM_ID);
  const msalService = inject(MsalService);
  const store = inject(Store<AppState>);
  const analyticsService = inject(AnalyticsService);

  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const requestedParticipantId = route.queryParams['participantId'];

  const isAuthenticated = store.selectSignal(selectIsAuthenticated)();
  const idTokenClaims = store.selectSignal(selectIdTokenClaims)();

  if (isAuthenticated && idTokenClaims) {
    return verifyAuthenticatedAccountMatchesRequest(
      idTokenClaims,
      requestedParticipantId,
      msalService,
      analyticsService,
    );
  }

  const router = inject(Router);
  return handleUnauthenticatedState(
    msalService,
    requestedParticipantId,
    router,
    store,
  );
};

async function verifyAuthenticatedAccountMatchesRequest(
  idTokenClaims: { sub?: string } | null | undefined,
  requestedParticipantId: string | null,
  msalService: MsalService,
  analyticsService: AnalyticsService,
): Promise<boolean | UrlTree> {
  const tokenParticipantId = extractParticipantIdFromToken(idTokenClaims);

  if (requestedParticipantId && tokenParticipantId !== requestedParticipantId) {
    await handleLogoutAndReauth(msalService, requestedParticipantId, analyticsService);
  }

  return true;
}

function handleUnauthenticatedState(
  msalService: MsalService,
  requestedParticipantId: string | null,
  router: Router,
  store: Store<AppState>,
): boolean | UrlTree {
  if (requestedParticipantId) {
    loginRedirect(msalService, requestedParticipantId);
    return false;
  }

  const joinCode = sessionStorage.getItem('joinCode');
  if (joinCode) {
    sessionStorage.removeItem('joinCode');
    return router.createUrlTree(['/join', joinCode]);
  }

  store.dispatch(
    MsalAuthActions.setErrorCode({
      errorCode: CustomAuthErrorCodes.ParticipantNotFound,
    }),
  );
  return router.createUrlTree(['/session-inaccessible']);
}

async function handleLogoutAndReauth(
  msalService: MsalService,
  requestedParticipantId: string,
  analyticsService: AnalyticsService,
): Promise<void> {
  await analyticsService.resetSession();
  msalService.instance.clearCache();
  msalService.instance.setActiveAccount(null);

  loginRedirect(msalService, requestedParticipantId, '/');
}

function loginRedirect(
  msalService: MsalService,
  participantId: string,
  startPage?: string | null,
): void {
  const loginRequest: RedirectRequest = {
    scopes: environment.msal.consentScopes,
    extraQueryParameters: {
      participantId,
    },
  };

  if (startPage) {
    loginRequest.redirectStartPage = startPage;
  }

  msalService.loginRedirect(loginRequest);
}
